<template>
  <div
    :class="
      `signin d-flex flex-column justify-content-center align-items-center h-100 p-4 ${
        !isSmallDevice ? `overflow-hidden` : null
      } scroll-content`
    "
  >
    <div class="login-form step-container p-4 scroll scroll-content-list">
      <span class="backButton" @click="goBack" style="width: 600px">
        <i class="fas fa-angle-left mr-1"></i> Back
      </span>
      <div class="d-flex flex-column justify-content-between text-center">
        <div class="d-flex justify-content-center text-center">
          <img
            class="secondary-logo pb-3"
            :src="this.config ? this.config.organizationLogo : ''"
            alt=""
          />
        </div>
        <div class="d-flex flex-column mb-4">
          <h3>{{ $t("selfAppointment.title") }}</h3>
        </div>
      </div>
      <appointment-request-steps
        v-model="currentStep"
        :steps="steps"
        ref="stepsComponent"
      />

      <single-selector-step
        v-if="currentStep === 0"
        v-model="services"
        @select="setService"
        :type="steps[currentStep]?.type"
        :existingPatient="patientId ? true : false"
        :isGoinBack="isGoinBack"
        @back="goBack"
        :loading="loading"
      />

      <single-selector-step
        v-if="currentStep === getStepIndex('location')"
        v-model="locations"
        @select="setLocation"
        @selectAny="setAnyLocation"
        type="location"
        @back="goBack"
        :loading="loading"
      />

      <single-selector-step
        v-if="currentStep === getStepIndex('placeOfService')"
        v-model="placesOfService"
        @select="setPlaceOfService"
        @selectAny="setAnyPlaceOfService"
        type="placeOfService"
        @back="goBack"
        :loading="loading"
      />

      <single-selector-step
        v-if="currentStep === getStepIndex('clinician')"
        v-model="clinicians"
        @select="setClinician"
        @selectAny="setAnyClinician"
        type="clinician"
        @back="goBack"
        :loading="loading"
      />

      <appointment-date-time-step
        v-if="currentStep === getStepIndex('datetime')"
        :isSmallDevice="isSmallDevice"
        :serviceDates="serviceDates"
        :serviceHours="serviceHours"
        :config="config"
        @setDate="setDate"
        @select="setHours"
        @back="goBack"
        :loading="loading"
      />

      <appointment-info-step
        v-if="currentStep === getStepIndex('info')"
        @setInfo="setInfo"
        :existingPatient="patientId ? true : false"
        :config="config"
        :insuranceEnabled="insuranceEnabled"
        @submitAppointmentRequest="submitAppointmentRequest"
      />

      <appointment-insurance-step
        v-if="currentStep === getStepIndex('insurance')"
        @submit="submitAppointmentRequest"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppointmentRequestSteps from "./steps/AppointmentRequestSteps.vue";
import AppointmentDateTimeStep from "./steps/AppointmentDateTimeStep.vue";
import SingleSelectorStep from "./steps/SingleSelectorStep.vue";
import AppointmentInfoStep from "./steps/AppointmentInfoStep.vue";
import AppointmentInsuranceStep from "./steps/AppointmentInsuranceStep.vue";
import { appointmentsServer } from "../../server/appointments";
import {
  addMinutesToDate,
  formatDateTimeSql,
  dateWhitFormat
} from "../../helpers/date";
export default {
  name: "AppointmentRequestAdd",
  components: {
    AppointmentRequestSteps,
    SingleSelectorStep,
    AppointmentInfoStep,
    AppointmentInsuranceStep,
    AppointmentDateTimeStep
  },
  data() {
    return {
      currentStep: 0,
      isGoinBack: false,
      steps: [],
      data: {},
      serviceHours: [],
      serviceDates: [],
      anyLocation: false,
      anyClinician: false,
      loading: false
    };
  },
  async mounted() {
    this.loading = true;
    setTimeout(() => {
      this.steps = this.getSteps;
    }, 500);
    this.fetchGenders();
    this.fetchStates();
    this.fetchServices(this.patientId ? true : false);
    await this.fetchLocations();
    this.fetchPlacesOfService();
    this.loading = false;
  },
  methods: {
    ...mapActions("client", [
      "fetchGenders",
      "fetchStates",
      "fetchServices",
      "fetchLocations",
      "fetchPlacesOfService",
      "fetchClinicians"
    ]),
    async fetchServiceDates(data) {
      const dates = await appointmentsServer.getServiceDates(data);
      this.serviceDates = dates;
    },
    async fetchServiceHours(data) {
      const hours = await appointmentsServer.getServiceHours(data);
      this.serviceHours = this.placeOfServiceEnabled
        ? hours.filter(hour => hour.placeOfServiceId)
        : hours;
    },
    setCurrentStep(value) {
      this.isGoinBack = this.currentStep > value;
      this.steps[value].result = null;
      this.loading = false;
      this.currentStep = value;
    },
    async setService(data) {
      this.loading = true;
      this.steps[this.currentStep].result = data.title;
      this.data.service = data.id;
      this.patientId && !this.placeOfServiceEnabled
        ? await this.fetchClinicians({
            serviceId: data.id,
            locationId: this.anyLocation ? null : this.locationId
          })
        : null;
      this.setCurrentStep(this.currentStep + 1);
    },
    async setLocation(data) {
      this.loading = true;
      this.steps[this.currentStep].result = data.name;
      this.data.location = this.anyLocation ? null : data.id;
      if (!this.placeOfServiceEnabled) {
        await this.fetchClinicians({
          serviceId: this.data.service,
          locationId: this.anyLocation ? null : this.data.location
        });
      }
      this.setCurrentStep(this.currentStep + 1);
    },
    async setPlaceOfService(data) {
      this.loading = true;
      this.steps[this.currentStep].result = data.name;

      this.data.placeOfService = this.anyLocation ? null : data.id;
      await this.fetchClinicians({
        serviceId: this.data.service,
        locationId:
          this.patientId && !this.placeOfServiceEnabled
            ? this.locationId
            : this.placeOfServiceEnabled
            ? null
            : this.anyLocation
            ? null
            : this.data.location,
        placeOfServiceId: this.data.placeOfService
      });
      this.setCurrentStep(this.currentStep + 1);
    },
    async setClinician(data) {
      this.loading = true;
      this.steps[this.currentStep].result = data.name;
      this.data.clinician = this.anyClinician ? null : data.id;
      await this.fetchServiceDates({
        providerId: this.anyClinician ? null : this.data.clinician,
        serviceId: this.data.service,
        locationId: this.anyLocation
          ? null
          : this.patientId
          ? this.placeOfServiceEnabled
            ? null
            : this.locationId
          : this.placeOfServiceEnabled
          ? null
          : this.data.location,
        placeOfServiceId: this.placeOfServiceEnabled
          ? this.data.placeOfService
          : null
      });
      this.setCurrentStep(this.currentStep + 1);
    },
    async setAnyLocation() {
      this.loading = true;
      this.anyLocation = true;
      this.steps[this.currentStep].result = "Any Available";
      if (!this.placeOfServiceEnabled) {
        await this.fetchClinicians({
          serviceId: this.data.service,
          locationId: this.anyLocation ? null : this.data.location
        });
      }
      this.setCurrentStep(this.currentStep + 1);
    },
    async setAnyPlaceOfService() {
      this.loading = true;
      this.anyLocation = true;
      this.steps[this.currentStep].result = "Any Available";
      await this.fetchClinicians({
        serviceId: this.data.service,
        locationId:
          this.patientId && !this.placeOfServiceEnabled
            ? this.locationId
            : this.placeOfServiceEnabled
            ? null
            : this.anyLocation
            ? null
            : this.data.location,
        placeOfServiceId: this.data.placeOfService
      });
      this.setCurrentStep(this.currentStep + 1);
    },
    async setAnyClinician() {
      this.loading = true;
      this.anyClinician = true;
      this.steps[this.currentStep].result = "Any Available";
      this.data.clinician = this.anyClinician ? null : this.data.clinician;
      await this.fetchServiceDates({
        providerId: this.anyClinician ? null : this.data.clinician,
        serviceId: this.data.service,
        locationId: this.anyLocation
          ? null
          : this.patientId
          ? this.placeOfServiceEnabled
            ? null
            : this.locationId
          : this.placeOfServiceEnabled
          ? null
          : this.data.location,
        placeOfServiceId: this.placeOfServiceEnabled
          ? this.data.placeOfService
          : null
      });
      this.data.location = this.anyLocation ? null : this.data.location;
      this.setCurrentStep(this.currentStep + 1);
    },
    async setDate(data) {
      this.loading = true;
      this.data.date = data;
      await this.fetchServiceHours({
        providerId: this.anyClinician ? null : this.data.clinician,
        serviceId: this.data.service,
        locationId:
          this.patientId && !this.placeOfServiceEnabled
            ? this.locationId
            : this.placeOfServiceEnabled
            ? null
            : this.anyLocation
            ? null
            : this.data.location,
        dateTime: this.data.date,
        placeOfServiceId: this.placeOfServiceEnabled
          ? this.data.placeOfService
          : null
      });
      this.loading = false;
    },
    setHours(data) {
      this.loading = true;
      this.data.placeOfService = null;
      const time = data.hour.split(":");
      const h = time[0];
      const m = time[1];
      this.data.date.setHours(h);
      this.data.date.setMinutes(m);

      this.data.location = data.locationId;
      this.data.clinician = data.providerId;
      if (this.placeOfServiceEnabled)
        this.data.placeOfService = data.placeOfServiceId;

      this.data.startDateTime = this.data.date;
      this.data.endDateTime = addMinutesToDate(
        this.data.startDateTime,
        this.getEventDuration
      );
      this.steps[this.currentStep].result = dateWhitFormat(
        this.data.date,
        "ddd, MMM DD, YYYY hh:mm A"
      );
      this.setCurrentStep(this.currentStep + 1);
    },
    setInfo(data) {
      this.steps[this.currentStep].result = "Done";
      this.data.info = data;
      this.setCurrentStep(this.currentStep + 1);
    },
    async submitAppointmentRequest(info, form) {
      this.data.form = form ?? {};
      this.data.info = info ?? this.data.info;
      if (this.patientId == null) {
        const appointmentInfo = await appointmentsServer.createAppointmentRequest(
          {
            firstName: this.data.info.firstName,
            middleName: this.data.info.middleName,
            lastName: this.data.info.lastName,
            preferredName: this.data.info.preferredName,
            ssn: this.data.info.ssn,
            dateOfBirth: new Date(this.data.info.dob),
            genderId: this.data.info.gender
              ? this.data.info.gender.value
              : null,
            phone: this.data.info.phone,
            landLineNumber: this.data.info.landLineNumber,
            contactPreference: this.data.info.contactPreference,
            email: this.data.info.email,
            addressLine1: this.data.info.addressLine1,
            addressLine2: this.data.info.addressLine2,
            city: this.data.info.city,
            stateId: this.data.info.state ? this.data.info.state.value : null,
            zipcode: this.data.info.zipcode,
            episodeOfCareId: null,
            providerId: this.data.clinician,
            startDateTime: formatDateTimeSql(this.data.startDateTime),
            endDateTime: formatDateTimeSql(this.data.endDateTime),
            serviceId: this.data.service,
            locationId: this.data.location,
            placeOfServiceId: this.data.placeOfService,
            insuranceId: this.data.form.company?.value,
            insuranceCardFrontBase64: this.data.form.insuranceCardFrontBase64,
            insuranceCardBackBase64: this.data.form.insuranceCardBackBase64,
            memberIdentifyer: this.data.form.member,
            groupNo: this.data.form.group,
            insuranceRelationshipId: this.data.form.relationship?.value,
            relationshipOther: this.data.form.relationshipOther,
            subscriberFirstName: this.data.form.relationshipInfo?.firstName,
            subscriberMiddleName: this.data.form.relationshipInfo?.middleName,
            subscriberLastName: this.data.form.relationshipInfo?.lastName,
            subscriberDateOfBirth: this.data.form.relationshipInfo?.dob,
            subscriberGenderId: this.data.form.relationshipInfo?.gender
              ? this.data.form.relationshipInfo?.gender.value
              : null,
            subscriberPhone: this.data.form.relationshipInfo?.phone,
            subscriberAddressLine1: this.data.form.relationshipInfo
              ?.addressLine1,
            subscriberAddressLine2: this.data.form.relationshipInfo
              ?.addressLine2,
            subscriberCity: this.data.form.relationshipInfo?.city,
            subscriberStateId: this.data.form.relationshipInfo?.state
              ? this.data.form.relationshipInfo?.state.value
              : null,
            subscriberZipcode: this.data.form.relationshipInfo?.zipcode,
            generalNotes: this.data.info.generalNotes,
            nameOnBehalfOf: this.data.info.nameOnBehalfOf,
            relationshipOnBehalfOfId: this.data.info.relationshipOnBehalfOf
              ?.value
          }
        );
        this.$router.replace({
          name: "AppointmentRequestFinished",
          params: {
            appointmentInfo: {
              data: appointmentInfo.data,
              newPatient: true,
              placeOfServiceEnabled: this.placeOfServiceEnabled
            }
          }
        });
      } else {
        const appointmentInfo = await appointmentsServer.createAppointmentRequest(
          {
            episodeOfCareId: this.patientId,
            providerId: this.data.clinician,
            startDateTime: formatDateTimeSql(this.data.startDateTime),
            endDateTime: formatDateTimeSql(this.data.endDateTime),
            serviceId: this.data.service,
            locationId: this.locationId,
            placeOfServiceId: this.data.placeOfService,
            existingPatient: true
          }
        );
        this.$router.replace({
          name: "AppointmentRequestFinished",
          params: {
            appointmentInfo: {
              data: appointmentInfo.data,
              newPatient: false,
              placeOfServiceEnabled: this.placeOfServiceEnabled
            }
          }
        });
      }
    },
    goBack() {
      if (this.currentStep === 0) {
        if (this.patientId) {
          this.$router.push("/appointments");
        } else {
          this.$router.push("/welcome");
        }
      } else {
        if (this.currentStep === 1) {
          this.anyLocation = false;
        }
        if (this.currentStep === 2) {
          this.anyClinician = false;
          this.anyLocation = false;
          this.data.placeOfService = null;
        }
        if (this.currentStep === 3) {
          this.anyClinician = false;
        }
        if (this.currentStep !== 5) {
          if (this.placeOfServiceEnabled && this.currentStep !== 3) {
            this.data.placeOfService = null;
          }
        }
        this.steps[this.currentStep].result = null;
        this.setCurrentStep(this.currentStep - 1);
      }
    },
    getStepIndex(type) {
      return this.getSteps.indexOf(
        this.getSteps.find(step => step.type === type)
      );
    }
  },
  computed: {
    ...mapState({
      services: state =>
        state.client.services.map(service => {
          return {
            id: service.id,
            title: service.name,
            duration: service.defaultDurationMinutes
          };
        }),
      locations: state =>
        state.client.locations.map(location => {
          return {
            id: location.id,
            name: location.name,
            address:
              location.addressLine1 +
              location.addressLine2 +
              ` ` +
              location.city +
              ` ` +
              location.zipcode
          };
        }),
      placesOfService: state =>
        state.client.placesOfService.map(placeOfService => {
          return {
            id: placeOfService.id,
            name: placeOfService.name
          };
        }),
      clinicians: state =>
        state.client.clinicians.map(clinician => {
          return {
            id: clinician.id,
            name: clinician.name
          };
        }),
      config: state => state.session.systemSettings,
      patientId: state => state.session.user.PatientId,
      locationId: state => state.session.location.id,
      anyLocationId: state => state.client.locations[0].id,
      insuranceEnabled: state =>
        state.session.systemSettings.appointmentRequestNewPatientInformation,
      placeOfServiceEnabled: state =>
        state.session.systemSettings.appointmentRequestPlaceOfServiceStepEnable
    }),
    isSmallDevice() {
      return window.innerWidth < 576 ? true : false;
    },
    getEventDuration() {
      switch (this.config.appointmentRequestPlaceTimeLogic) {
        case "OnTheFullHour":
          return 60;
        case "OnTheHalfHour":
          return 30;
        case "OnTheQuarterHour":
          return 15;
        default:
          return this.services.find(service => service.id === this.data.service)
            .duration;
      }
    },
    getSteps() {
      let steps = [
        {
          title: this.$t("step-service"),
          result: null,
          type: "service"
        },
        {
          title: this.$t("step-location"),
          result: null,
          type: "location"
        },
        {
          title: this.$t("step-place-of-service"),
          result: null,
          type: "placeOfService"
        },
        {
          title: this.$t("step-clinician"),
          result: null,
          type: "clinician"
        },
        {
          title: this.$t("step-date-and-time"),
          result: null,
          type: "datetime"
        },
        {
          title: this.$t("insurance"),
          result: null,
          type: "insurance"
        }
      ];

      if (this.patientId) {
        steps.splice(steps.length - 1, 0, {
          title: this.$t("step-info-confirm"),
          result: null,
          type: "info"
        });
        if (this.placeOfServiceEnabled) {
          steps = steps.filter(
            step => step.type !== "insurance" && step.type !== "location"
          );
        } else {
          steps = steps.filter(
            step =>
              step.type !== "insurance" &&
              step.type !== "location" &&
              step.type !== "placeOfService"
          );
        }
      } else {
        steps.splice(steps.length - 1, 0, {
          title: this.$t("step-info"),
          result: null,
          type: "info"
        });
        if (this.placeOfServiceEnabled) {
          steps = steps.filter(step => step.type !== "location");
        } else {
          steps = steps.filter(step => step.type !== "placeOfService");
        }
        if (!this.insuranceEnabled) {
          steps = steps.filter(step => step.type !== "insurance");
        }
      }

      return steps;
    }
  }
};
</script>

<style lang="scss" scoped>
.secondary-logo {
  height: 100px;
}

.backButton {
  cursor: pointer;
  color: #208de2;
  font-size: 18px;
}
.signin {
  position: relative;
  background: linear-gradient(
    to bottom,
    #13609b,
    #208de2 47.5%,
    #f5f6f9 47.5%,
    #f5f6f9
  );

  .logo-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, calc(-50% - 235px));
  }
}

.step-container {
  max-width: 1024px;
  width: 100%;

  max-height: min-content;
  min-height: 400px;

  .lang-select {
    float: left;
    font-size: 0.7em;
    max-width: 100px;
    margin-top: 2em;
  }

  input {
    font-size: 0.8em;
  }
}

.scroll-content {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  overflow: hidden;
}

.scroll-content::-webkit-scrollbar {
  display: none;
}

.scroll {
  overflow-y: auto;
}

.scroll-content-list::-webkit-scrollbar {
  width: 5px;
  height: 5px;
  background-color: #dfe6e9;
  overflow-y: auto;
}
.scroll-content-list::-webkit-scrollbar-thumb {
  background-color: #2196f3;
  border-radius: 10px;
}
</style>
